import React from 'react';
import { User, ChevronUp, ChevronDown, Check } from 'lucide-react';
import LocationDropdown from './LocationDropdown';

export default function ClientDetailsSection({ filters, onChange, isExpanded, onToggleExpanded, onClear }) {
    const isActive = () => {
        const cd = filters.clientDetails || {};
        return (
            cd.paymentVerified ||
            cd.minSpend || cd.minHires ||
            cd.minRating || cd.minReviews ||
            (cd.preferredLocations && cd.preferredLocations.length > 0) ||
            (cd.avoidLocations && cd.avoidLocations.length > 0)
        );
    };

    const handleAddLocation = (type, country) => {
        const currentList = filters.clientDetails?.[type] || [];
        if (!currentList.includes(country)) {
            onChange({
                ...filters,
                clientDetails: {
                    ...filters.clientDetails,
                    [type]: [...currentList, country]
                }
            });
        }
    };

    const handleRemoveLocation = (type, country) => {
        const currentList = filters.clientDetails?.[type] || [];
        onChange({
            ...filters,
            clientDetails: {
                ...filters.clientDetails,
                [type]: currentList.filter(c => c !== country)
            }
        });
    };

    return (
        <div className="bg-white p-4 rounded-xl border border-gray-100 h-fit">
            <button
                type="button"
                className={`w-full flex items-center justify-between cursor-pointer ${isExpanded ? 'mb-4' : ''}`}
                onClick={onToggleExpanded}
            >
                <div className="flex items-center gap-2 font-semibold text-gray-700">
                    <User size={20} className="text-gray-400" />
                    <span>Client details</span>
                </div>
                <div className="flex items-center gap-2">
                    {isActive() && (
                        <span className="bg-blue-100 text-blue-600 p-1 rounded-md flex items-center justify-center">
                            <Check size={14} strokeWidth={3} />
                        </span>
                    )}
                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
            </button>

            {isExpanded && (
                <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-200">

                    {isActive() && (
                        <div className="flex justify-end -mt-2">
                            <button
                                type="button"
                                onClick={onClear}
                                className="text-xs font-medium text-gray-400 hover:text-red-600 focus:outline-none transition-colors"
                            >
                                Clear
                            </button>
                        </div>
                    )}

                    <div className="space-y-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={filters.clientDetails?.paymentVerified || false}
                                onChange={(e) => onChange({
                                    ...filters,
                                    clientDetails: { ...filters.clientDetails, paymentVerified: e.target.checked }
                                })}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">Payment method verified</span>
                        </label>

                        <label className="flex items-center gap-2 cursor-pointer text-gray-400 pointer-events-none opacity-60"> {/* Disabled/Mocked for now */}
                            <input
                                type="checkbox"
                                disabled
                                className="rounded border-gray-300 text-gray-400"
                            />
                            <span className="text-sm">Enterprise Client</span>
                        </label>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label htmlFor="jf-min-spend" className="block text-sm font-medium text-gray-900 mb-1">
                                Total spend, USD:
                            </label>
                            <input
                                id="jf-min-spend"
                                type="number"
                                placeholder="0"
                                value={filters.clientDetails?.minSpend || ''}
                                onChange={(e) => onChange({
                                    ...filters,
                                    clientDetails: { ...filters.clientDetails, minSpend: e.target.value }
                                })}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 hover:border-blue-300 transition-colors"
                            />
                        </div>
                        <div>
                            <label htmlFor="jf-min-hires" className="block text-sm font-medium text-gray-900 mb-1">
                                Hires:
                            </label>
                            <input
                                id="jf-min-hires"
                                type="number"
                                placeholder="0"
                                value={filters.clientDetails?.minHires || ''}
                                onChange={(e) => onChange({
                                    ...filters,
                                    clientDetails: { ...filters.clientDetails, minHires: e.target.value }
                                })}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 hover:border-blue-300 transition-colors"
                            />
                        </div>
                        <div>
                            <label htmlFor="jf-min-rating" className="block text-sm font-medium text-gray-900 mb-1">
                                Rating:
                            </label>
                            <input
                                id="jf-min-rating"
                                type="number"
                                placeholder="0"
                                step="0.1"
                                max="5"
                                value={filters.clientDetails?.minRating || ''}
                                onChange={(e) => onChange({
                                    ...filters,
                                    clientDetails: { ...filters.clientDetails, minRating: e.target.value }
                                })}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 hover:border-blue-300 transition-colors"
                            />
                        </div>
                        <div>
                            <label htmlFor="jf-min-reviews" className="block text-sm font-medium text-gray-900 mb-1">
                                Reviews count:
                            </label>
                            <input
                                id="jf-min-reviews"
                                type="number"
                                placeholder="0"
                                value={filters.clientDetails?.minReviews || ''}
                                onChange={(e) => onChange({
                                    ...filters,
                                    clientDetails: { ...filters.clientDetails, minReviews: e.target.value }
                                })}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 hover:border-blue-300 transition-colors"
                            />
                        </div>
                    </div>

                    <LocationDropdown
                        id="jf-preferred-locations"
                        label="Preferred Client Locations:"
                        type="preferredLocations"
                        selected={filters.clientDetails?.preferredLocations}
                        activeClassName="bg-blue-100 text-blue-700"
                        tagClassName="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs flex items-center gap-1"
                        tagButtonClassName="hover:text-blue-900"
                        tagRemoveAriaPrefix="Remove preferred location"
                        onAdd={handleAddLocation}
                        onRemove={handleRemoveLocation}
                    />

                    <LocationDropdown
                        id="jf-avoid-locations"
                        label="Avoid Clients From:"
                        type="avoidLocations"
                        selected={filters.clientDetails?.avoidLocations}
                        activeClassName="bg-red-50 text-red-700"
                        tagClassName="bg-red-50 text-red-700 px-2 py-1 rounded text-xs flex items-center gap-1"
                        tagButtonClassName="hover:text-red-900"
                        tagRemoveAriaPrefix="Remove avoid location"
                        onAdd={handleAddLocation}
                        onRemove={handleRemoveLocation}
                    />

                    <label className="flex items-center gap-2 cursor-pointer pt-2">
                        <input
                            type="checkbox"
                            checked={filters.clientDetails?.includeNoHistory ?? true}
                            onChange={(e) => onChange({
                                ...filters,
                                clientDetails: { ...filters.clientDetails, includeNoHistory: e.target.checked }
                            })}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Include clients without sufficient history</span>
                    </label>

                </div>
            )}
        </div>
    );
}
